import { useState } from 'react';
import { Grid, Tab, Tabs } from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import PDFViewer from 'src/components/pdf-viewer/PDFViewer';
import OCRResultsTab from './OCRResultsTab';
import ExtractedDataTab from './ExtractedDataTab';
import { Button } from 'src/components/ui/button';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { XlsxViewer } from '@/components/xlsx-viewer/XlsxViewer';
import { EmailAttachment, EmailViewerProps } from './email-viewer-types';
import { isExcelFile } from './utils';
import { ImageWithSmartScaling } from '@/components/common/ImageWithSmartScaling';
import DOMPurify from 'dompurify';

const hideDataTabs = true;

const EmailViewer: React.FC<EmailViewerProps> = ({
  emailData,
  fetchOCR,
  fetchExtractedPurchaseOrder,
  defaultTab = 'email',
}) => {
  const inboundEmailId = emailData.inboundEmailId;

  const [activeTab, setActiveTab] = useState(defaultTab === 'email' ? 0 : 1);
  const hasAttachments =
    !!emailData.attachments?.length || !!emailData.documentAssociations?.length;
  const showOCRTab = !!fetchOCR && !!inboundEmailId;
  const showExtractedDataTab =
    !!fetchExtractedPurchaseOrder && !!inboundEmailId;

  const renderEmailHeader = () => (
    <MDBox
      mb={3}
      p={3}
      border="1px solid"
      borderColor="grey.300"
      borderRadius="borderRadius"
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MDBox mb={2}>
            <MDTypography
              variant="caption"
              color="text"
              sx={{
                fontWeight: '600',
                fontSize: '14px',
                display: 'block',
                mb: 0.5,
              }}
            >
              Subject:
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                fontSize: '14px',
                color: 'text.primary',
              }}
            >
              {emailData.subject}
            </MDTypography>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox mb={2}>
            <MDTypography
              variant="caption"
              color="text"
              sx={{
                fontWeight: '600',
                fontSize: '14px',
                display: 'block',
                mb: 0.5,
              }}
            >
              From:
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                fontSize: '14px',
                color: 'text.primary',
              }}
            >
              {emailData.from}
            </MDTypography>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox mb={2}>
            <MDTypography
              variant="caption"
              color="text"
              sx={{
                fontWeight: '600',
                fontSize: '14px',
                display: 'block',
                mb: 0.5,
              }}
            >
              To:
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                fontSize: '14px',
                color: 'text.primary',
              }}
            >
              {emailData.to}
            </MDTypography>
          </MDBox>
        </Grid>
        {emailData.cc && (
          <Grid item xs={12}>
            <MDBox mb={2}>
              <MDTypography
                variant="caption"
                color="text"
                sx={{
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                CC:
              </MDTypography>
              <MDTypography
                variant="body2"
                sx={{
                  wordBreak: 'break-word',
                  fontSize: '14px',
                  color: 'text.primary',
                }}
              >
                {emailData.cc}
              </MDTypography>
            </MDBox>
          </Grid>
        )}
        {emailData.date && (
          <Grid item xs={12}>
            <MDBox>
              <MDTypography
                variant="caption"
                color="text"
                sx={{
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Date:
              </MDTypography>
              <MDTypography
                variant="body2"
                sx={{
                  wordBreak: 'break-word',
                  fontSize: '14px',
                  color: 'text.primary',
                }}
              >
                {new Date(emailData.date).toLocaleString()}
              </MDTypography>
            </MDBox>
          </Grid>
        )}
      </Grid>
    </MDBox>
  );

  const renderEmailBody = () => {
    const htmlContent = emailData.htmlContent;
    const textContent = emailData.textContent;

    // Check if we have any content at all
    const hasHtmlContent =
      htmlContent &&
      htmlContent
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
        .trim().length > 0;

    const hasTextContent = textContent && textContent.trim().length > 0;

    const hasAnyContent = hasHtmlContent || hasTextContent;

    if (!hasAnyContent) {
      return (
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={4}
          border="1px solid"
          borderColor="grey.300"
          borderRadius="borderRadius"
        >
          <MDTypography
            variant="caption2"
            color="text.secondary"
            fontStyle="italic"
            sx={{
              opacity: 0.2,
              letterSpacing: 0.5,
            }}
          >
            No email body content
          </MDTypography>
        </MDBox>
      );
    }

    // Render HTML content if available
    if (hasHtmlContent) {
      // Replace cid: references with actual attachment URLs
      let processedHtml = htmlContent;

      const attachments = emailData.attachments || [];
      const documentAssociations = emailData.documentAssociations || [];
      const allAttachments = [
        ...attachments,
        ...documentAssociations.map((doc) => ({
          id: doc.document.id,
          fileName: doc.document.originalFileName || doc.document.name,
          s3Url: doc.document.url,
          contentId: doc.document.contentId || null,
        })),
      ];

      if (allAttachments.length > 0) {
        allAttachments.forEach((attachment) => {
          let replaced = false;

          // Try matching by contentId first (if available)
          if (attachment.contentId) {
            const cidPattern = new RegExp(`cid:${attachment.contentId}`, 'gi');
            const beforeReplace = processedHtml;
            processedHtml = processedHtml.replace(cidPattern, attachment.s3Url);
            if (beforeReplace !== processedHtml) {
              replaced = true;
            }
          }

          // Try matching by ID (for UUID-based CID references)
          if (!replaced && 'id' in attachment && attachment.id) {
            const cidPattern = new RegExp(`cid:${attachment.id}`, 'gi');
            const beforeReplace = processedHtml;
            processedHtml = processedHtml.replace(cidPattern, attachment.s3Url);
            if (beforeReplace !== processedHtml) {
              replaced = true;
            }
          }

          // Fallback: Match by filename pattern (e.g., cid:image003.png@... matches image003.png)
          if (!replaced) {
            const escapedFilename = attachment.fileName.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&',
            );
            const cidPattern = new RegExp(
              `cid:${escapedFilename}@[^"'\\s>]+`,
              'gi',
            );
            processedHtml = processedHtml.replace(cidPattern, attachment.s3Url);
          }
        });
      }

      return (
        <MDBox
          p={3}
          mt={'-15px'}
          border="1px solid"
          borderColor="grey.300"
          borderRadius="borderRadius"
          sx={{
            '& img': { maxWidth: '100%' },
            '& a': { color: 'info.main' },
            overflowX: 'auto',
            bgcolor: 'white',
            '& p': {
              marginBottom: '1em',
              lineHeight: 1.6,
              fontSize: '14px',
              color: 'text.primary',
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              marginBottom: '0.5em',
              color: 'text.primary',
            },
            '& ul, & ol': {
              paddingLeft: '1.5em',
              marginBottom: '1em',
            },
            '& blockquote': {
              borderLeft: '4px solid',
              borderColor: 'grey.300',
              paddingLeft: '1em',
              marginLeft: 0,
              marginBottom: '1em',
              fontStyle: 'italic',
            },
            '& pre': {
              backgroundColor: 'grey.50',
              padding: '1em',
              borderRadius: '4px',
              overflowX: 'auto',
              marginBottom: '1em',
            },
            '& code': {
              backgroundColor: 'grey.50',
              padding: '0.2em 0.4em',
              borderRadius: '3px',
              fontSize: '0.9em',
            },
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(processedHtml),
          }}
        />
      );
    }

    // Fallback to text content with proper line break handling
    return (
      <MDBox
        p={3}
        mt={'-15px'}
        border="1px solid"
        borderColor="grey.300"
        borderRadius="borderRadius"
        bgcolor="white"
      >
        <MDTypography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '14px',
            color: 'text.primary',
            lineHeight: 1.6,
          }}
        >
          {textContent}
        </MDTypography>
      </MDBox>
    );
  };

  const renderAttachments = () => {
    const attachments = emailData.attachments || [];
    const documentAssociations = emailData.documentAssociations || [];

    console.log({ attachments, documentAssociations });

    if (!hasAttachments) {
      return (
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={4}
          border="1px solid"
          borderColor="grey.300"
          borderRadius="borderRadius"
        >
          <MDTypography
            variant="caption2"
            color="text.secondary"
            fontStyle="italic"
            sx={{
              opacity: 0.2,
              letterSpacing: 0.5,
            }}
          >
            No attachments found
          </MDTypography>
        </MDBox>
      );
    }

    const isPdf = (attachment: EmailAttachment) =>
      attachment.mimeType === 'application/pdf' ||
      attachment.fileName?.toLowerCase().endsWith('.pdf');

    const sortedAttachments = [
      ...(emailData.attachments || []),
      ...(emailData.documentAssociations || []).map((doc) => ({
        id: doc.id,
        fileName: doc.document.originalFileName || doc.document.name,
        sizeBytes: doc.document.fileSizeBytes,
        mimeType: doc.document.mimeType,
        s3Url: doc.document.url,
      })),
    ].sort((a, b) => {
      const aIsPdf = isPdf(a);
      const bIsPdf = isPdf(b);
      if (aIsPdf && !bIsPdf) return -1;
      if (!aIsPdf && bIsPdf) return 1;
      return 0;
    });

    console.log({ sortedAttachments });

    return (
      <MDBox p={2}>
        {sortedAttachments.map((attachment) => (
          <MDBox key={attachment.id} mb={2}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <MDTypography
                variant="subtitle2"
                sx={{ wordBreak: 'break-word' }}
              >
                {attachment.fileName} (
                {(attachment.sizeBytes / 1024).toFixed(2)} KB)
              </MDTypography>
              <Button
                variant="link"
                onClick={() => window.open(attachment.s3Url, '_blank')}
                style={{
                  justifyContent: 'flex-start',
                  padding: '4px 8px',
                  height: 'auto',
                }}
              >
                <ArrowSquareOutIcon className="w-4 h-4 mr-2" />
              </Button>
            </MDBox>
            {attachment.mimeType?.startsWith('image/') ? (
              <ImageWithSmartScaling
                src={attachment.s3Url}
                alt={attachment.fileName}
                fileName={attachment.fileName}
                sizeBytes={attachment.sizeBytes}
              />
            ) : attachment.mimeType === 'application/pdf' ||
              attachment.fileName?.toLowerCase().endsWith('.pdf') ? (
              <PDFViewer url={attachment.s3Url} />
            ) : isExcelFile(attachment) ? (
              <XlsxViewer
                url={attachment.s3Url}
                fileName={attachment.fileName}
              />
            ) : (
              <MDTypography
                component="a"
                href={attachment.s3Url}
                target="_blank"
                rel="noopener noreferrer"
                color="info"
              >
                Download File
              </MDTypography>
            )}
          </MDBox>
        ))}
      </MDBox>
    );
  };

  return (
    <MDBox sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <MDBox sx={{ flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 2,
            '& .MuiTab-root': {
              minWidth: '120px',
              width: '120px',
            },
          }}
        >
          <Tab label="Email" />
          <Tab
            label={
              <MDBox display="flex" alignItems="center">
                Attachments
                <MDBox
                  component="span"
                  ml={0.75}
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  width={16}
                  height={16}
                  fontSize="0.65rem"
                  fontWeight="medium"
                  color="text.secondary"
                  bgcolor="grey.200"
                  borderRadius="50%"
                  sx={{
                    position: 'relative',
                    top: '0.5px',
                    lineHeight: 1,
                  }}
                >
                  (
                  {emailData.attachments?.length ||
                    emailData.documentAssociations?.length ||
                    0}
                  )
                </MDBox>
              </MDBox>
            }
          />
          {!hideDataTabs && showOCRTab && <Tab label="OCR" />}
          {!hideDataTabs && showExtractedDataTab && <Tab label="Data" />}
        </Tabs>
      </MDBox>

      <MDBox sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {activeTab === 0 && (
          <MDBox p={2}>
            {renderEmailHeader()}
            {renderEmailBody()}
          </MDBox>
        )}

        {activeTab === 1 && renderAttachments()}

        {showOCRTab && activeTab === 2 && (
          <OCRResultsTab
            inboundEmailId={emailData.inboundEmailId}
            attachments={emailData.attachments}
          />
        )}

        {showExtractedDataTab && activeTab === (showOCRTab ? 3 : 2) && (
          <ExtractedDataTab
            inboundEmailId={emailData.inboundEmailId}
            hasAttachments={hasAttachments}
          />
        )}
      </MDBox>
    </MDBox>
  );
};

export default EmailViewer;
